import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Stack,
    Box,
    Divider,
    Collapse,
    IconButton,
    Chip,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import useCurrency from '../../../hooks/common/useCurrency';
import PaymentStatusBadge from '../Table/PaymentStatusBadge';

const RefundHistory = ({ history }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const [expanded, setExpanded] = useState(false);

    if (!Array.isArray(history) || history.length === 0) return null;

    const total = history.reduce((acc, h) => acc + (parseFloat(h.amount) || 0), 0);

    return (
        <Box sx={{ mt: 1 }}>
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    cursor: 'pointer',
                    py: 0.5,
                    px: 1,
                    borderRadius: 1.5,
                    bgcolor: (t) =>
                        t.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.04)'
                            : 'rgba(0,0,0,0.03)',
                    '&:hover': {
                        bgcolor: (t) =>
                            t.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.08)'
                                : 'rgba(0,0,0,0.06)',
                    },
                }}
            >
                <Chip
                    size="small"
                    label={history.length}
                    color="warning"
                    sx={{
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        height: 20,
                        minWidth: 28,
                        '& .MuiChip-label': { px: 0.75, fontSize: 11 },
                    }}
                />
                <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
                    {isArabic
                        ? `استرداد بإجمالي ${formatPrice(total)}`
                        : `refunds totaling ${formatPrice(total)}`}
                </Typography>
                <IconButton size="small" sx={{ p: 0 }}>
                    {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <Stack
                    spacing={1}
                    sx={{
                        mt: 1,
                        pl: 1.5,
                        borderInlineStart: (t) => `2px solid ${t.palette.warning.main}33`,
                    }}
                >
                    {history.map((h, idx) => (
                        <Box key={idx}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" fontWeight="bold" color="warning.main">
                                    - {formatPrice(h.amount ?? 0)}
                                </Typography>
                                {h.at && (
                                    <Typography variant="caption" color="text.secondary">
                                        {dayjs(h.at).format('YYYY-MM-DD HH:mm')}
                                    </Typography>
                                )}
                            </Stack>
                            {h.reason && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {h.reason}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                                {h.gateway_ref && (
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? 'مرجع:' : 'Ref:'} {h.gateway_ref}
                                    </Typography>
                                )}
                                {(h.actor?.name || h.actor_name) && (
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? 'بواسطة' : 'by'} {h.actor?.name || h.actor_name}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
};

const OrderPaymentCard = ({ order }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const payments = order?.payments || [];
    if (payments.length === 0) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 2,
            }}
        >
            <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                {isArabic ? 'الدفع' : 'Payment'}
            </Typography>

            <Stack divider={<Divider flexItem />} spacing={2}>
                {payments.map((p) => (
                    <Box key={p.id}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight="medium">
                                {p.provider || p.method || '-'}
                            </Typography>
                            <PaymentStatusBadge status={p.status} />
                        </Stack>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? 'المبلغ:' : 'Amount:'} {formatPrice(p.amount ?? 0)}
                            </Typography>
                            {(p.refunded_amount ?? 0) > 0 && (
                                <Typography variant="caption" color="warning.main" fontWeight="medium">
                                    {isArabic ? 'المسترد:' : 'Refunded:'} {formatPrice(p.refunded_amount)}
                                </Typography>
                            )}
                            {p.provider_reference && (
                                <Typography variant="caption" color="text.secondary">
                                    {isArabic ? 'مرجع:' : 'Ref:'} {p.provider_reference}
                                </Typography>
                            )}
                            {p.paid_at && (
                                <Typography variant="caption" color="text.secondary">
                                    {isArabic ? 'تاريخ الدفع:' : 'Paid at:'} {dayjs(p.paid_at).format('YYYY-MM-DD HH:mm')}
                                </Typography>
                            )}
                        </Stack>

                        <RefundHistory history={p.refund_history} />
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
};

export default OrderPaymentCard;
